import dayjs from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

export type ESignatureFieldType = 'text' | 'signature' | 'radio' | 'date'

export type ESignatureFieldAttributes = {
  options?: string[]
  autoGenerate?: boolean
  readOnly?: boolean
  [key: string]: unknown
}

@Serializable()
export class ESignatureTemplateField extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('eSignatureTemplateId')
  eSignatureTemplateId!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('type')
  type!: ESignatureFieldType

  @JsonProperty('fieldAttributes')
  fieldAttributes!: ESignatureFieldAttributes

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string
}

@Serializable()
export class ESignatureTemplate extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('templateKey')
  templateKey!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('filePath')
  filePath!: string

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty({
    path: 'eSignatureTemplateField',
    elementType: ESignatureTemplateField,
  })
  eSignatureTemplateField!: ESignatureTemplateField[]
}

export type SignatureFieldValues = Record<string, string>

export interface MembershipESignatureDocument {
  id: string
  eSignatureTemplateId: string
  eSignatureTemplateName: string
  eSignatureTemplateKey: string
  userMembershipId: number
  userId: number
  fileName: string | null
  filePath: string | null
  dateSigned: string | null
  cancelledAt: string | null
  createdAt: string
  updatedAt: string
  documentVersionId: string | null
  documentVersion: number | null
  documentName: string | null
}

@Serializable()
export class SignerInputDto extends Fromable {
  @JsonProperty('fieldName')
  fieldName?: string

  @JsonProperty('fullName')
  fullName?: string

  @JsonProperty('email')
  email?: string

  @JsonProperty('signedAt')
  signedAt?: string

  @JsonProperty('signatureDataUrl')
  signatureDataUrl?: string
}

@Serializable()
export class FillPlaceAndSignDto extends Fromable {
  @JsonProperty('fields')
  fields!: SignatureFieldValues

  @JsonProperty('templateKey')
  templateKey!: string

  @JsonProperty('addVisibleWidget')
  addVisibleWidget?: boolean

  @JsonProperty('signatureLength')
  signatureLength?: number

  @JsonProperty({ path: 'signers', elementType: SignerInputDto })
  signers?: SignerInputDto[]
}

@Serializable()
export class ESignatureDocument extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('userSigningId')
  userSigningId!: number

  @JsonProperty('eSignatureTemplateId')
  eSignatureTemplateId!: string

  @JsonProperty('data')
  data!: Record<string, unknown> | null

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty('eSignatureTemplateName')
  eSignatureTemplateName!: string

  @JsonProperty('eSignatureTemplateKey')
  eSignatureTemplateKey!: string

  @JsonProperty('userMembershipId')
  userMembershipId!: number

  @JsonProperty('fileName')
  fileName?: string | null

  @JsonProperty('dateSigned')
  dateSigned?: string | null

  @JsonProperty('cancelledAt')
  cancelledAt?: string | null

  @JsonProperty('documentVersionId')
  documentVersionId?: string | null

  @JsonProperty('documentVersion')
  documentVersion?: number | null

  @JsonProperty('documentName')
  documentName?: string | null

  public DataForTable() {
    const dataRecord: Record<string, unknown> = this.data ?? {}
    const status = typeof dataRecord['status'] === 'string' ? dataRecord['status'] : undefined
    const packageName =
      typeof dataRecord['packageName'] === 'string' ? dataRecord['packageName'] : undefined
    const signedAtRaw = (() => {
      if (typeof this.dateSigned === 'string' && this.dateSigned.trim().length) {
        return this.dateSigned
      }
      if (typeof dataRecord['signedAt'] === 'string' && dataRecord['signedAt'].trim().length) {
        return dataRecord['signedAt']
      }
      return undefined
    })()
    const cancelledAtRaw = (() => {
      if (typeof this.cancelledAt === 'string' && this.cancelledAt.trim().length) {
        return this.cancelledAt
      }
      if (
        typeof dataRecord['cancelledAt'] === 'string' &&
        dataRecord['cancelledAt'].trim().length
      ) {
        return dataRecord['cancelledAt']
      }
      return undefined
    })()
    const dateSigned = signedAtRaw ? dayjs(signedAtRaw).format('DD MMM YYYY HH:mm:ss') : null
    const cancelledAt = cancelledAtRaw ? dayjs(cancelledAtRaw).format('DD MMM YYYY HH:mm:ss') : null
    const fileNameCandidates = [
      // Prefer the config doc's own name; fileName now carries the package name
      // for config membership docs, so it must not win the label here.
      typeof this.documentName === 'string' ? this.documentName : undefined,
      typeof dataRecord['documentName'] === 'string' ? dataRecord['documentName'] : undefined,
      typeof this.fileName === 'string' ? this.fileName : undefined,
      typeof dataRecord['fileName'] === 'string' ? dataRecord['fileName'] : undefined,
    ]
    const fileName = fileNameCandidates.find((candidate) => candidate && candidate.trim().length)

    const normalizedStatus = (() => {
      if (dateSigned) return 'SIGNED'
      if (cancelledAt) return 'CANCELLED'
      if (typeof status === 'string' && status.trim().length) {
        return status.trim().toUpperCase()
      }
      return 'CREATED'
    })()

    return {
      id: this.id,
      createdAt: this.createdAt ? dayjs(this.createdAt).format('DD MMM YYYY HH:mm:ss') : '-',
      type: this.eSignatureTemplateName || '-',
      fileName: fileName ?? '-',
      status: normalizedStatus,
      packageName: packageName ?? '-',
      dateSigned: dateSigned ?? null,
      dateSignedRaw: signedAtRaw ?? null,
      cancelledAt: cancelledAt ?? null,
      cancelledAtRaw: cancelledAtRaw ?? null,
    }
  }
}
